import React, { useState } from 'react';
import { GitCommit, FileText, Clock, Download, AlertTriangle, RotateCcw, ChevronRight, ChevronDown, Github, ExternalLink } from 'lucide-react';
import { useWorkspaceCommits } from '../../hooks/useWorkspaceCommits';
import { useWorkspaceCommitDetails } from '../../hooks/useWorkspaceCommitDetails';
import useRepositoryDownload from '../../hooks/useRepositoryDownload';
import { useResetToCommit } from '../../hooks/useResetToCommit';
import { formatDistanceToNow } from 'date-fns';
import { useWorkspaceRunningJobs } from '../../hooks/useWorkspaceRunningJobs';
import { Spinner } from '../../design-system';

interface RevisionSectionProps {
  workspace: any;
  workspaceId: string;
}

const RepositoryDownloadButton: React.FC<{ workspace: any }> = ({ workspace }) => {
  const { downloadRepository, isDownloading, error, clearError } = useRepositoryDownload();

  // Only show for sandbox workspaces
  if (!workspace.is_sandbox) return null;

  const handleDownload = async () => {
    try {
      clearError();
      await downloadRepository(workspace.id);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <div className="relative w-full sm:w-auto">
      <button
        onClick={handleDownload}
        disabled={isDownloading}
        className="w-full sm:w-auto rounded-xl px-4 py-2 flex items-center justify-center text-xs sm:text-sm font-medium transition-all duration-200 shadow-sm bg-brand-action hover:bg-brand-action-hover text-brand-action-text disabled:opacity-50 disabled:bg-slate-300 dark:disabled:bg-neutral-600 disabled:text-ui-text-tertiary dark:disabled:text-ui-text-tertiary"
        title="Download repository as ZIP"
      >
        <Download size={14} className="mr-1.5" />
        <span>{isDownloading ? 'Downloading...' : 'Download ZIP'}</span>
      </button>

      {/* Error Message */}
      {error && (
        <div className="absolute top-full left-0 mt-1 p-2 bg-red-100 dark:bg-red-900 border border-red-300 dark:border-red-700 rounded text-xs text-red-700 dark:text-red-300 z-10 whitespace-nowrap">
          {error}
        </div>
      )}
    </div>
  );
};

const RevisionSection: React.FC<RevisionSectionProps> = ({ workspace, workspaceId }) => {
  const { hasActiveJobs: hasRunningJobs } = useWorkspaceRunningJobs(workspaceId);
  const { commits, loading, error, refetch } = useWorkspaceCommits(workspaceId, 50);
  const { resetToCommit, resetting, error: resetError, clearError } = useResetToCommit();
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [selectedCommit, setSelectedCommit] = useState<any>(null);
  const [resetResult, setResetResult] = useState<any>(null);
  const { fetchDetails, getCommitFiles, isLoading: isCommitLoading } = useWorkspaceCommitDetails(workspaceId);
  const [expandedCommits, setExpandedCommits] = useState<Set<string>>(new Set());

  const handleResetClick = (commit: any) => {
    setSelectedCommit(commit);
    setShowResetDialog(true);
    setResetResult(null);
  };

  const handleConfirmReset = async () => {
    if (!selectedCommit) return;
    
    const result = await resetToCommit(workspaceId, selectedCommit.id);
    
    if (result) {
      setResetResult(result);
      setTimeout(() => {
        refetch();
        setShowResetDialog(false);
        setSelectedCommit(null);
        setResetResult(null);
      }, 2000);
    }
  };

  const handleCommitClick = async (commit: any) => {
    const isExpanded = expandedCommits.has(commit.id);
    
    if (!isExpanded) {
      setExpandedCommits(prev => new Set(prev).add(commit.id));
      // Fetch details if we don't have them yet
      const existingFiles = getCommitFiles(commit.id);
      if (existingFiles.length === 0 && !isCommitLoading(commit.id)) {
        await fetchDetails(commit.id);
      }
    } else {
      setExpandedCommits(prev => {
        const newSet = new Set(prev);
        newSet.delete(commit.id);
        return newSet;
      });
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-ui-text-primary">Revision History</h3>
            <p className="text-sm text-ui-text-secondary mt-1">
              View and manage your project's commit history
            </p>
          </div>
          {workspace && (
            <div className="w-full sm:w-auto sm:flex-shrink-0">
              <RepositoryDownloadButton workspace={workspace} />
            </div>
          )}
        </div>
      </div>

      {/* GitHub Repository Info */}
      {workspace?.git_provider === 'github' && workspace?.github_repo_full_name && (
        <div className="mb-4 bg-ui-background-tertiary border border-ui-border-primary rounded-xl p-3 sm:p-4">
          <div className="flex items-center justify-between gap-2 sm:gap-3">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
              <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-ui-background-secondary rounded-lg border border-ui-border-primary flex-shrink-0">
                <Github className="w-4 h-4 sm:w-5 sm:h-5 text-ui-text-primary" />
              </div>
              <div className="min-w-0">
                <div className="text-xs text-ui-text-tertiary mb-0.5">
                  GitHub Repository
                </div>
                <div className="font-medium text-sm sm:text-base text-ui-text-primary truncate">
                  {workspace.github_repo_full_name}
                </div>
              </div>
            </div>
            <a
              href={`https://github.com/${workspace.github_repo_full_name}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center space-x-1 sm:space-x-1.5 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-ui-text-secondary bg-ui-background-secondary hover:bg-ui-background-tertiary border border-ui-border-primary rounded-lg transition-colors whitespace-nowrap flex-shrink-0"
              title="View on GitHub"
            >
              <span className="hidden sm:inline">View on GitHub</span>
              <span className="sm:hidden">View</span>
              <ExternalLink className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            </a>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto bg-ui-background-tertiary dark:bg-neutral-800/50 rounded-xl border border-ui-border-primary">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Spinner size={32} className="mx-auto mb-4" />
              <p className="text-sm text-ui-text-tertiary">Loading commit history...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-red-500 dark:text-red-400">Failed to load commits</p>
          </div>
        ) : (
          <div className="divide-y divide-ui-border-primary">
            {commits.length === 0 ? (
              <div className="p-4 text-center text-ui-text-tertiary">
                No commits found
              </div>
            ) : (
              commits.map((commit, index) => {
                const isExpanded = expandedCommits.has(commit.id);
                const isLoadingFiles = isExpanded && isCommitLoading(commit.id);
                const displayFiles = getCommitFiles(commit.id);
                
                return (
                  <div key={commit.id} className="p-4 hover:bg-ui-background-tertiary transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <div 
                          className="flex items-start cursor-pointer"
                          onClick={() => handleCommitClick(commit)}
                        >
                          <div className="mr-2 mt-0.5">
                            {isExpanded ? (
                              <ChevronDown size={16} className="text-ui-text-tertiary" />
                            ) : (
                              <ChevronRight size={16} className="text-ui-text-tertiary" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-ui-text-primary mb-1">
                              {commit.message}
                            </p>
                            <div className="flex flex-wrap items-center text-xs text-ui-text-tertiary gap-3 mb-2">
                              <span className="flex items-center">
                                <Clock size={12} className="mr-1" />
                                {formatDistanceToNow(new Date(commit.date), { addSuffix: true })}
                              </span>
                              <span className="flex items-center">
                                <GitCommit size={12} className="mr-1" />
                                {commit.id}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Show files when expanded */}
                        <div className={`mt-2 ml-6 overflow-hidden transition-all duration-200 ease-in-out ${isExpanded ? 'max-h-96' : 'max-h-0 opacity-0'}`}>
                          {isExpanded && isLoadingFiles ? (
                            <>
                              {[1, 2, 3].map((placeholder) => (
                                <div key={`skeleton-${placeholder}`} className="flex items-center text-xs text-ui-text-secondary mb-1 animate-pulse">
                                  <div className="h-3 w-3 mr-1.5 bg-ui-background-tertiary rounded opacity-70"></div>
                                  <div className="h-3 w-[70%] bg-ui-background-tertiary rounded opacity-70"></div>
                                  <div className="flex ml-auto">
                                    <div className="h-3 w-6 ml-2 bg-green-200 dark:bg-green-800/30 rounded opacity-70"></div>
                                    <div className="h-3 w-6 ml-1 bg-red-200 dark:bg-red-800/30 rounded opacity-70"></div>
                                  </div>
                                </div>
                              ))}
                            </>
                          ) : displayFiles.length > 0 ? (
                            <>
                              {displayFiles.slice(0, 5).map((file: any, fileIndex: number) => (
                                <div key={`${file.filename}-${fileIndex}`} className="flex items-center text-xs text-ui-text-secondary mb-1">
                                  <FileText size={12} className="mr-1.5 text-ui-text-tertiary" />
                                  <span className="truncate flex-1">{file.filename}</span>
                                  {(file.additions > 0 || file.deletions > 0) && (
                                    <>
                                      <span className="text-green-600 dark:text-green-400 ml-2">+{file.additions}</span>
                                      <span className="text-red-600 dark:text-red-400 ml-1">-{file.deletions}</span>
                                    </>
                                  )}
                                </div>
                              ))}
                              {displayFiles.length > 5 && (
                                <span className="text-xs text-ui-text-tertiary">
                                  ... and {displayFiles.length - 5} more files
                                </span>
                              )}
                            </>
                          ) : (
                            <div className="text-xs text-ui-text-tertiary">
                              No file changes
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Reset button - don't show for the latest commit */}
                      {index > 0 && workspace?.is_sandbox && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResetClick(commit);
                          }}
                          disabled={resetting || hasRunningJobs}
                          className="ml-2 p-2 text-ui-text-tertiary hover:text-red-600 dark:text-ui-text-tertiary dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all group disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-ui-text-primary dark:disabled:hover:text-ui-text-primary disabled:hover:bg-transparent"
                          title={
                            hasRunningJobs
                              ? 'Cannot reset while threads are running'
                              : `Reset to commit ${commit.id}`
                          }
                        >
                          <RotateCcw size={16} className="group-hover:rotate-[-20deg] transition-transform" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Reset Confirmation Dialog */}
      {showResetDialog && selectedCommit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-ui-background-secondary rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center mb-4 text-red-600 dark:text-red-500">
              <AlertTriangle size={24} className="mr-2" />
              <h3 className="text-lg font-semibold">Permanently Reset Repository</h3>
            </div>
            
            <p className="text-ui-text-secondary mb-4">
              Are you sure you want to reset the repository to this commit? This will:
            </p>
            
            <ul className="list-disc list-inside text-sm text-ui-text-secondary mb-4 space-y-1">
              <li>Permanently delete all commits after this point</li>
              <li>Remove all changes made after this commit</li>
              <li>This action cannot be undone</li>
            </ul>
            
            <div className="bg-ui-background-tertiary dark:bg-ui-background-tertiary rounded-lg p-3 mb-4">
              <p className="text-sm font-medium text-ui-text-primary">
                Reset to: {selectedCommit.message}
              </p>
              <p className="text-xs text-ui-text-secondary mt-1">
                Commit: {selectedCommit.id}
              </p>
              <p className="text-xs text-ui-text-secondary">
                Date: {formatDistanceToNow(new Date(selectedCommit.date), { addSuffix: true })}
              </p>
            </div>

            {resetError && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-sm text-red-700 dark:text-red-300">
                <div className="font-medium mb-1">Error:</div>
                <div className="whitespace-pre-wrap">{resetError}</div>
              </div>
            )}

            {resetResult && (
              <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded text-sm text-green-700 dark:text-green-300">
                <p className="font-medium">{resetResult.message}</p>
                {resetResult.removed_commits > 0 && (
                  <p className="text-xs mt-1">Removed {resetResult.removed_commits} commit(s)</p>
                )}
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowResetDialog(false);
                  setSelectedCommit(null);
                  setResetResult(null);
                  clearError();
                }}
                disabled={resetting}
                className="px-4 py-2 text-ui-text-secondary bg-ui-background-tertiary hover:bg-ui-background-tertiary rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReset}
                disabled={resetting || hasRunningJobs || !!resetResult}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resetting ? 'Resetting...' : resetResult ? 'Done!' : 'Reset to This Commit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevisionSection; 